// src/pages/AuthorizationsPending.jsx
// Rediseño visual aplicado 2026-05-13. Lógica preservada 1:1.
import React, { useEffect, useState, useCallback, useContext } from 'react';
import { Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import socket from '../services/socket';
import { AuthContext } from '../context/AuthContext';


const Icon = ({ children, size = 16, stroke = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {children}
    </svg>
);
const I = {
    search:    <Icon size={14}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></Icon>,
    x:         <Icon size={14}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Icon>,
    check:     <Icon size={14}><path d="M20 6 9 17l-5-5" /></Icon>,
    clock:     <Icon size={14}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Icon>,
    checkCircle: <Icon size={14}><circle cx="12" cy="12" r="9" /><path d="m9 12 2 2 4-4" /></Icon>,
    xCircle:   <Icon size={14}><circle cx="12" cy="12" r="9" /><path d="M15 9l-6 6M9 9l6 6" /></Icon>,
    layers:    <Icon size={14}><path d="m12 2 9 5-9 5-9-5 9-5Z" /><path d="m3 12 9 5 9-5" /><path d="m3 17 9 5 9-5" /></Icon>,
    arrowRight: <Icon size={12}><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></Icon>,
    shield:    <Icon size={14}><path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5l-8-3Z" /></Icon>,
};


const AUTH_CFG = {
    none:     { label: 'Sin autorización', color: 'var(--c-text-3)', bg: 'var(--c-status-closed-bg)', dot: 'var(--c-text-3)' },
    pending:  { label: 'Pendiente',        color: '#b45309', bg: 'var(--c-status-progress-bg)', dot: '#d97706' },
    approved: { label: 'Aprobado',         color: '#047857', bg: 'var(--c-priority-low-bg)', dot: '#059669' },
    rejected: { label: 'Rechazado',        color: '#b91c1c', bg: 'var(--c-priority-urgent-bg)', dot: '#dc2626' },
};
const PRIORITY_CFG = {
    low:    { label: 'Baja',    color: '#047857', bg: 'var(--c-priority-low-bg)' },
    medium: { label: 'Media',   color: '#1d4ed8', bg: 'var(--c-status-open-bg)' },
    high:   { label: 'Alta',    color: '#b45309', bg: 'var(--c-status-progress-bg)' },
    urgent: { label: 'Urgente', color: '#b91c1c', bg: 'var(--c-priority-urgent-bg)' },
};


function fmt(date) {
    if (!date) return null;
    return new Date(date).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
}

function resolveMySlot(ticket, uid) {
    const id1 = String(ticket.authorizer?._id  || ticket.authorizer  || '');
    const id2 = String(ticket.authorizer2?._id || ticket.authorizer2 || '');
    const id3 = String(ticket.authorizer3?._id || ticket.authorizer3 || '');

    if (id1 === uid) return { slot: 1, status: ticket.authorizerStatus  || 'pending', decidedAt: ticket.authorizerDecidedAt  };
    if (id2 === uid) return { slot: 2, status: ticket.authorizer2Status || 'pending', decidedAt: ticket.authorizer2DecidedAt };
    if (id3 === uid) return { slot: 3, status: ticket.authorizer3Status || 'pending', decidedAt: ticket.authorizer3DecidedAt };
    return { slot: 0, status: 'pending', decidedAt: null };
}


const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
.ap-root {
    --ap-accent: var(--brand-700, #0a4aa8);
    --ap-accent-hover: var(--brand-800, #07357f);
    --ap-accent-ring: rgba(10, 74, 168, .12);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    max-width: 1200px; margin: 0 auto; padding: 24px 20px 60px;
    color: var(--c-text-1);
}
.ap-root * { box-sizing: border-box; }
.ap-title { font-size: 24px; font-weight: 700; margin: 0 0 4px; letter-spacing: -0.01em; }
.ap-subtitle { color: var(--c-text-3); font-size: 13.5px; margin: 0 0 24px; line-height: 1.5; }

.ap-kpi-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 14px; margin-bottom: 24px;
}
.ap-kpi {
    background: var(--c-card); border: 1px solid var(--c-border);
    border-left: 4px solid var(--kpi-color, var(--ap-accent));
    border-radius: 14px;
    padding: 18px 20px;
    box-shadow: 0 1px 2px rgba(15,23,42,.04);
    transition: transform .15s, box-shadow .15s;
}
.ap-kpi:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(15,23,42,.06); }
.ap-kpi-head {
    display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;
}
.ap-kpi-label {
    font-size: 11px; font-weight: 600; letter-spacing: 0.5px;
    text-transform: uppercase; color: var(--c-text-4); margin: 0;
}
.ap-kpi-icon {
    color: var(--kpi-color, var(--ap-accent));
    display: inline-flex; align-items: center; justify-content: center;
    width: 28px; height: 28px; border-radius: 8px;
    background: var(--kpi-bg, var(--c-msg-bg));
}
.ap-kpi-value {
    font-size: 30px; font-weight: 800; color: var(--c-text-1);
    margin: 0; line-height: 1; letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
}

/* Tabs */
.ap-tabs-row {
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 16px; flex-wrap: wrap;
}
.ap-tabs {
    display: inline-flex; gap: 4px; padding: 4px;
    background: var(--c-card);
    border: 1px solid var(--c-border);
    border-radius: 999px;
}
.ap-tab {
    background: transparent; border: none;
    padding: 7px 16px; border-radius: 999px;
    font-size: 13px; font-weight: 600;
    color: var(--c-text-3);
    cursor: pointer; font-family: inherit;
    display: inline-flex; align-items: center; gap: 6px;
    transition: all .15s;
}
.ap-tab:hover { color: var(--c-text-1); background: var(--c-msg-bg); }
.ap-tab.active { background: var(--ap-accent); color: #fff; }
.ap-tab.active:hover { background: var(--ap-accent-hover); }
.ap-tab-count {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 18px; height: 18px; padding: 0 5px;
    border-radius: 999px;
    background: rgba(255,255,255,.25);
    font-size: 10px; font-weight: 700;
}
.ap-tab:not(.active) .ap-tab-count {
    background: var(--c-msg-bg); color: var(--c-text-3);
}

/* Search */
.ap-search {
    flex: 0 1 320px;
    display: flex; align-items: center; gap: 8px;
    padding: 7px 12px;
    background: var(--c-input-bg);
    border: 1px solid var(--c-border);
    border-radius: 999px;
    color: var(--c-text-3);
    transition: border-color .15s, box-shadow .15s, background .15s;
}
.ap-search:focus-within {
    border-color: var(--ap-accent);
    box-shadow: 0 0 0 3px var(--ap-accent-ring);
    background: var(--c-card);
}
.ap-search input {
    flex: 1; border: none; outline: none; background: transparent;
    color: var(--c-text-1); font-size: 13px; font-family: inherit;
    min-width: 0;
}
.ap-search-clear {
    background: none; border: none; cursor: pointer;
    color: var(--c-text-4); padding: 2px; line-height: 1;
    display: inline-flex; align-items: center; border-radius: 4px;
    transition: color .15s;
}
.ap-search-clear:hover { color: var(--c-text-1); }

/* Table */
.ap-table-wrap {
    background: var(--c-card); border: 1px solid var(--c-border);
    border-radius: 14px; overflow: hidden;
    box-shadow: 0 1px 2px rgba(15,23,42,.04);
}
.ap-table {
    width: 100%; border-collapse: collapse;
    font-size: 13px;
}
.ap-table thead th {
    background: var(--c-msg-bg);
    padding: 10px 16px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    color: var(--c-text-4);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid var(--c-card-header-border);
    white-space: nowrap;
}
.ap-table tbody td {
    padding: 12px 16px;
    border-bottom: 1px solid var(--c-card-header-border);
    vertical-align: middle;
}
.ap-table tbody tr:last-child td { border-bottom: none; }
.ap-table tbody tr:hover { background: var(--c-msg-bg); }

.ap-folio {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-weight: 600; color: var(--c-text-2);
    background: var(--c-msg-bg); padding: 3px 9px; border-radius: 6px;
    font-size: 11px; letter-spacing: 0.3px;
    display: inline-block;
}
.ap-title-cell {
    font-weight: 600; color: var(--c-text-1);
}
.ap-title-cell a { color: inherit; text-decoration: none; }
.ap-title-cell a:hover { color: var(--ap-accent); }
.ap-slot-tag {
    margin-left: 8px; font-size: 10px; font-weight: 700;
    color: var(--c-text-4); text-transform: uppercase; letter-spacing: 0.4px;
}

.ap-pill {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 999px;
    font-weight: 600; font-size: 11px; line-height: 1.4;
}
.ap-pill-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }

.ap-decided-time { font-size: 11px; color: var(--c-text-4); margin-top: 3px; }

.ap-action-cell { display: flex; gap: 6px; justify-content: flex-end; }

.ap-btn {
    display: inline-flex; align-items: center; gap: 4px;
    border: 1px solid transparent; border-radius: 7px;
    padding: 5px 11px; font-size: 12px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    transition: all .15s; text-decoration: none;
}
.ap-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.ap-btn-success { background: #059669; color: #fff; border-color: #059669; }
.ap-btn-success:hover:not(:disabled) { background: #047857; border-color: #047857; }
.ap-btn-danger { background: #dc2626; color: #fff; border-color: #dc2626; }
.ap-btn-danger:hover:not(:disabled) { background: #b91c1c; border-color: #b91c1c; }
.ap-btn-ghost {
    background: transparent; color: var(--ap-accent); border-color: var(--c-border);
}
.ap-btn-ghost:hover:not(:disabled) {
    background: var(--c-msg-bg); border-color: var(--ap-accent);
}

.ap-empty {
    text-align: center; padding: 60px 20px; margin: 0;
    color: var(--c-text-4); font-size: 13.5px;
}

@media (max-width: 820px) {
    .ap-table thead { display: none; }
    .ap-table tbody td { padding: 8px 12px; }
}
`;


function Pill({ status, cfg }) {
    const c = cfg[status] || { label: status, color: 'var(--c-text-3)', bg: 'var(--c-status-closed-bg)', dot: 'var(--c-text-3)' };
    return (
        <span className="ap-pill" style={{ background: c.bg, color: c.color }}>
            {c.dot && <span className="ap-pill-dot" style={{ background: c.dot }} />}
            {c.label}
        </span>
    );
}


export default function AuthorizationsPending() {
    const { user } = useContext(AuthContext);
    const [tickets,      setTickets]      = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [tab,          setTab]          = useState('pending');
    const [search,       setSearch]       = useState('');
    const [acting,       setActing]       = useState(null);

    const uid = String(user?.id || user?.userId || '');

    const load = useCallback(async () => {
        if (!uid) return;
        setLoading(true);
        try {
            const { data } = await api.get('/tickets', { params: { limit: 200, requiresAuthorization: true } });
            const mine = (data.items || []).filter(t => resolveMySlot(t, uid).slot > 0);
            setTickets(mine);
        } catch { toast.error('No se pudieron cargar las autorizaciones'); }
        finally { setLoading(false); }
    }, [uid]);

    useEffect(() => {
        load();
        const onUpdated = (updated) => {
            if (!updated.requiresAuthorization) return;
            setTickets(prev => {
                const exists = prev.some(t => String(t._id) === String(updated._id));
                if (exists) return prev.map(t => String(t._id) === String(updated._id) ? updated : t);
                const { slot } = resolveMySlot(updated, uid);
                return slot > 0 ? [updated, ...prev] : prev;
            });
        };
        socket.on('ticketUpdated', onUpdated);
        return () => socket.off('ticketUpdated', onUpdated);
    }, [load, uid]);

    const handleAuthorize = async (ticketId, decision) => {
        setActing(ticketId);
        try {
            const { data } = await api.patch(`/tickets/${ticketId}/authorize`, { decision });
            setTickets(prev => prev.map(t => String(t._id) === String(data._id) ? data : t));
            toast.success(decision === 'approved' ? 'Ticket aprobado' : 'Ticket rechazado');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al procesar autorización');
        } finally { setActing(null); }
    };

    const pending  = tickets.filter(t => resolveMySlot(t, uid).status === 'pending');
    const resolved = tickets.filter(t => resolveMySlot(t, uid).status !== 'pending');

    const kpis = [
        { label: 'Pendientes (tu decisión)', value: pending.length,                                                           color: '#d97706', bg: 'var(--c-status-progress-bg)', icon: I.clock },
        { label: 'Aprobados por ti',         value: resolved.filter(t => resolveMySlot(t, uid).status === 'approved').length, color: '#059669', bg: 'var(--c-priority-low-bg)',     icon: I.checkCircle },
        { label: 'Rechazados por ti',        value: resolved.filter(t => resolveMySlot(t, uid).status === 'rejected').length, color: '#dc2626', bg: 'var(--c-priority-urgent-bg)',  icon: I.xCircle },
        { label: 'Total asignados',          value: tickets.length,                                                           color: '#2563eb', bg: 'var(--c-status-open-bg)',      icon: I.layers },
    ];

    const activeList = tab === 'pending' ? pending : resolved;
    const filtered = search.trim()
        ? activeList.filter(t => {
            const q = search.trim().toLowerCase();
            return t.title?.toLowerCase().includes(q) ||
                   String(t.folio).includes(q) ||
                   t.department?.name?.toLowerCase().includes(q);
          })
        : activeList;

    return (
        <>
            <style>{CSS}</style>
            <div className="ap-root">

                <h1 className="ap-title">Autorizaciones</h1>
                <p className="ap-subtitle">Tickets asignados a ti para revisión y autorización</p>

                <div className="ap-kpi-grid">
                    {kpis.map(k => (
                        <div key={k.label} className="ap-kpi" style={{ '--kpi-color': k.color, '--kpi-bg': k.bg }}>
                            <div className="ap-kpi-head">
                                <p className="ap-kpi-label">{k.label}</p>
                                <span className="ap-kpi-icon">{k.icon}</span>
                            </div>
                            <p className="ap-kpi-value">{k.value}</p>
                        </div>
                    ))}
                </div>

                <div className="ap-tabs-row">
                    <div className="ap-tabs">
                        <button type="button" className={`ap-tab${tab === 'pending' ? ' active' : ''}`} onClick={() => setTab('pending')}>
                            Pendientes
                            <span className="ap-tab-count">{pending.length}</span>
                        </button>
                        <button type="button" className={`ap-tab${tab === 'resolved' ? ' active' : ''}`} onClick={() => setTab('resolved')}>
                            Resueltos
                            <span className="ap-tab-count">{resolved.length}</span>
                        </button>
                    </div>

                    <div className="ap-search">
                        {I.search}
                        <input
                            type="text"
                            placeholder="Buscar por folio, título…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        {search && (
                            <button type="button" className="ap-search-clear" onClick={() => setSearch('')} aria-label="Limpiar">
                                {I.x}
                            </button>
                        )}
                    </div>
                </div>

                <div className="ap-table-wrap">
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 60 }}><Spinner animation="border" variant="primary" /></div>
                    ) : filtered.length === 0 ? (
                        <p className="ap-empty">
                            {tab === 'pending'
                                ? 'No tienes tickets pendientes de autorización.'
                                : 'Aún no has autorizado ningún ticket.'}
                        </p>
                    ) : (
                        <table className="ap-table">
                            <thead>
                                <tr>
                                    <th>Folio</th>
                                    <th>Título</th>
                                    <th>Departamento</th>
                                    <th>Prioridad</th>
                                    <th>Estado general</th>
                                    <th>Tu decisión</th>
                                    <th style={{ textAlign: 'right' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(t => {
                                    const { slot, status: myStatus, decidedAt: myDecidedAt } = resolveMySlot(t, uid);
                                    const canDecide = myStatus === 'pending' && t.authorizationStatus === 'pending';
                                    const isActing  = acting === t._id;

                                    return (
                                        <tr key={t._id}>
                                            <td><span className="ap-folio">#{t.folio || '—'}</span></td>
                                            <td className="ap-title-cell">
                                                <Link to={`/tickets/${t._id}`}>{t.title}</Link>
                                                {slot > 0 && (
                                                    <span className="ap-slot-tag">Aut. {slot}</span>
                                                )}
                                            </td>
                                            <td style={{ color: 'var(--c-text-3)' }}>{t.department?.name || '—'}</td>
                                            <td><Pill status={t.priority} cfg={PRIORITY_CFG} /></td>
                                            <td><Pill status={t.authorizationStatus} cfg={AUTH_CFG} /></td>
                                            <td>
                                                <Pill status={myStatus} cfg={AUTH_CFG} />
                                                {myDecidedAt && (
                                                    <div className="ap-decided-time">{fmt(myDecidedAt)}</div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="ap-action-cell">
                                                    {canDecide ? (
                                                        <>
                                                            <button type="button" className="ap-btn ap-btn-success" disabled={isActing}
                                                                onClick={() => handleAuthorize(t._id, 'approved')}>
                                                                {isActing ? <Spinner size="sm" animation="border" /> : <>{I.check} Aprobar</>}
                                                            </button>
                                                            <button type="button" className="ap-btn ap-btn-danger" disabled={isActing}
                                                                onClick={() => handleAuthorize(t._id, 'rejected')}>
                                                                {isActing ? <Spinner size="sm" animation="border" /> : <>{I.x} Rechazar</>}
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <Link to={`/tickets/${t._id}`} className="ap-btn ap-btn-ghost">
                                                            Ver detalle {I.arrowRight}
                                                        </Link>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </>
    );
}
